import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import api from "../api";
import useReveal from "../hooks/useReveal";

const rate = (p) => `${p.commission_pct}%${p.commission_fixed ? ` + ${Math.round(p.commission_fixed * 100)}p` : ""} per sale`;

export default function Pricing() {
  const [data, setData] = useState(null);
  const { ref, cls } = useReveal();
  useEffect(() => { api.get("/plans").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data || data.plans.length === 0) return null;
  const plans = [...data.plans].sort((a, b) => a.monthly_fee - b.monthly_fee);
  const trialPlan = plans.find((p) => p.id === data.trial_plan_id);
  return (
    <section id="pricing-plans" className="bg-[#f6f8fb] py-28" data-testid="pricing-section">
      <div ref={ref} className={`max-w-[1150px] mx-auto px-6 ${cls}`}>
        <h2 className="gp-heading text-[2.4rem] sm:text-[3rem] text-center">Simple pricing that grows with you</h2>
        <p className="text-[#5a6b80] text-lg text-center mt-4">
          {data.trial_days > 0 && trialPlan ? `Every new business gets ${data.trial_days} days of ${trialPlan.name} free. No card needed.` : "No setup fees. No minimum term."}
        </p>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {plans.map((p, i) => {
            const hl = i === plans.length - 1;
            return (
              <div key={p.id} data-testid={`pricing-plan-${p.id}`} style={{ transitionDelay: `${i * 80}ms` }}
                className={`rounded-3xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${hl ? "bg-[#16243b] text-white shadow-[0_30px_70px_rgba(20,30,55,0.3)]" : "bg-white text-[#16243b] shadow-[0_10px_30px_rgba(20,30,55,0.07)]"}`}>
                <h3 className="text-xl font-bold">{p.name}</h3>
                <p className={`text-sm mt-1 min-h-[40px] ${hl ? "text-white/70" : "text-[#8a97a8]"}`}>{p.tagline}</p>
                <p className="mt-6"><span className="text-5xl font-bold">£{p.monthly_fee % 1 ? p.monthly_fee.toFixed(2) : p.monthly_fee}</span><span className={hl ? "text-white/60" : "text-[#8a97a8]"}>/mo</span></p>
                <p className={`font-medium mt-1 ${hl ? "text-[#2ec9c9]" : "text-[#1fb0b0]"}`}>{rate(p)}</p>
                <ul className="mt-7 space-y-2.5 text-sm flex-1">
                  {data.base_included.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-[#2ec9c9] shrink-0 mt-0.5" />{f}</li>)}
                  {data.features.filter((f) => p.features.includes(f.key)).map((f) => (
                    <li key={f.key} className="flex gap-2"><Check size={16} className="text-[#2ec9c9] shrink-0 mt-0.5" />
                      <span>{f.label}{f.coming_soon && <span className="ml-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5">Coming soon</span>}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" data-testid={`pricing-cta-${p.id}`}
                  className={`mt-8 rounded-full py-3.5 text-center font-medium ${hl ? "gp-btn-teal" : "gp-btn-outline"}`}>
                  {data.trial_days > 0 ? "Start free trial" : "Get started"}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
